/**
 * Logo Showcase section: desktop layout reset after resize and mobile a11y updates.
 * Swiper init is handled by s-carousel in theme.js (belowDesktop only).
 */

(function () {
  'use strict';

  const DESKTOP_MEDIA_QUERY = '(min-width: 64.01rem)';
  let resizeTimer;

  function isDesktop() {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  }

  function resetDesktopLayout(section) {
    const carouselWrapper = section.querySelector('s-carousel');
    const carouselEl = section.querySelector('[js-carousel]');
    const track = section.querySelector('.logo-showcase__track');
    const slides = section.querySelectorAll('.logo-showcase__item');

    if (carouselEl?.swiper?.destroy) {
      // cleanStyles must stay false: Swiper's cleanStyles wipes the whole style
      // attribute on each slide, including the Liquid-authored --logo-showcase-item-*
      // custom properties that cap logo sizes. Swiper's inline styles are removed
      // individually below instead.
      carouselEl.swiper.destroy(true, false);
    }

    slides.forEach(function (slide) {
      [
        'width',
        'height',
        'margin-right',
        'margin-left',
        'margin-inline-start',
        'margin-inline-end',
      ].forEach(function (property) {
        slide.style.removeProperty(property);
      });
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('inert');
    });

    if (track) {
      track.style.removeProperty('transform');
      track.style.removeProperty('width');
      track.style.removeProperty('transition-duration');
    }

    if (carouselEl) {
      carouselEl.style.removeProperty('width');
      carouselEl.style.removeProperty('height');
    }

    if (carouselWrapper) {
      delete carouselWrapper.dataset.a11yInit;
    }

    const liveRegion = section.querySelector('[data-logo-showcase-status]');
    if (liveRegion) {
      liveRegion.textContent = '';
    }
  }

  function syncSectionLayout(section) {
    const desktop = isDesktop();
    section.classList.toggle('logo-showcase--desktop', desktop);

    if (desktop) {
      resetDesktopLayout(section);
    }
  }

  function updateSlideAccessibility(carouselWrapper, swiper) {
    if (!swiper?.slides || isDesktop()) return;

    const slideCount =
      parseInt(carouselWrapper.dataset.slideCount, 10) || swiper.slides.length;

    swiper.slides.forEach(function (slide) {
      if (!(slide instanceof HTMLElement)) return;

      const visible = slide.classList.contains('swiper-slide-visible');

      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');

      if (visible) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });

    const liveRegion = carouselWrapper.querySelector('[data-logo-showcase-status]');
    if (!liveRegion) return;

    const activeIndex =
      typeof swiper.activeIndex === 'number' ? swiper.activeIndex + 1 : 1;
    const template = carouselWrapper.dataset.slideStatusTemplate;

    liveRegion.textContent = template
      ? template
          .replace('__INDEX__', String(activeIndex))
          .replace('__COUNT__', String(slideCount))
      : 'Slide ' + activeIndex + ' of ' + slideCount;
  }

  function bindSwiper(section, carouselWrapper, carouselEl) {
    const swiper = carouselEl.swiper;
    if (!swiper || carouselWrapper.dataset.a11yInit === 'true' || isDesktop()) return;

    carouselWrapper.dataset.a11yInit = 'true';

    const sync = function () {
      updateSlideAccessibility(carouselWrapper, swiper);
    };

    sync();
    swiper.on('slideChange', sync);
    swiper.on('resize', sync);
    swiper.on('breakpoint', sync);
  }

  function initLogoShowcase(section) {
    if (!section || section.dataset.logoShowcaseInited) return;
    section.dataset.logoShowcaseInited = 'true';

    const carouselWrapper = section.querySelector('s-carousel');
    const carouselEl = section.querySelector('[js-carousel]');

    if (!carouselEl || !carouselWrapper) return;

    syncSectionLayout(section);

    function handleReady(event) {
      if (event.detail?.swiper && !isDesktop()) {
        bindSwiper(section, carouselWrapper, carouselEl);
      }
    }

    if (carouselEl.swiper && !isDesktop()) {
      bindSwiper(section, carouselWrapper, carouselEl);
    } else if (carouselWrapper) {
      carouselWrapper.addEventListener('carousel:ready', handleReady);
    }
  }

  function syncAllLayouts() {
    document.querySelectorAll('[data-logo-showcase]').forEach(syncSectionLayout);
  }

  function handleDesktopChange() {
    window.requestAnimationFrame(syncAllLayouts);
  }

  function initAll(container) {
    const root = container || document;
    root.querySelectorAll('[data-logo-showcase]').forEach(initLogoShowcase);
  }

  const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  desktopMediaQuery.addEventListener('change', handleDesktopChange);

  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(syncAllLayouts, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('[data-logo-showcase]');
    if (!section) return;

    delete section.dataset.logoShowcaseInited;
    section.classList.remove('logo-showcase--desktop');

    const carouselWrapper = section.querySelector('s-carousel');
    if (carouselWrapper) {
      delete carouselWrapper.dataset.a11yInit;
    }

    const carouselEl = section.querySelector('[js-carousel].swiper-initialized');
    if (carouselEl?.swiper?.destroy) {
      // cleanStyles false — see resetDesktopLayout
      carouselEl.swiper.destroy(true, false);
    }
  });
})();
